import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Stack, Typography, Skeleton, Alert, Button, IconButton, Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  PrintRounded as PrintIcon,
  ReceiptLongRounded as ReceiptIcon,
  ZoomInRounded as ZoomInIcon,
  ZoomOutRounded as ZoomOutIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import { P, money } from './portalTheme';
import { combinedHTML, RECEIPT_CSS } from '../pages/payments/ReceiptDialog';

// ─────────────────────────────────────────────────────────────────
// ReceiptSlips — the receipts as documents, read like a PDF.
//
// A page rail down the left, the slips stacked down the middle. Scroll
// and they run one into the next; click a thumbnail and the middle
// jumps to it; scroll the middle and the rail follows. That is the
// interaction everybody already knows from a PDF reader, so there is
// nothing to explain.
//
// THE MARKUP IS THE CASHIER'S
//   `combinedHTML` is imported from the staff ReceiptDialog rather than
//   reimplemented. A parent's copy of a receipt that differs from the
//   school's copy — even in a heading — is a dispute waiting to happen,
//   and a lookalike would drift the first time the header changes.
//
// WHY dangerouslySetInnerHTML
//   The receipt is built as a string of HTML with inline styles so that
//   what prints is byte-identical to what is on screen. Every value in
//   it goes through `esc()` on the way in. Rebuilding it as JSX would
//   mean two renderers to keep in step, which is the thing this file
//   exists to avoid.
// ─────────────────────────────────────────────────────────────────

const PAGE = 15;
// The sheet is 210mm wide. At a typical 96dpi that is ~794px, which
// will not fit a phone — so it is scaled down rather than clipped.
const SHEET_PX = 794;

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d).slice(0, 10)
    : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function ReceiptSlips() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [data, setData]       = useState(null);
  const [slips, setSlips]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore]       = useState(false);
  const [error, setError]     = useState('');
  const [active, setActive]   = useState(0);
  const [zoom, setZoom]       = useState(1);

  const scrollRef = useRef(null);
  const pageRefs  = useRef([]);
  const railRefs  = useRef([]);

  const fetchPage = (offset) =>
    API.get('/portal/receipts', { params: { limit: PAGE, offset } });

  useEffect(() => {
    let alive = true;
    fetchPage(0)
      .then(r => { if (alive) { setData(r.data); setSlips(r.data?.receipts || []); setError(''); } })
      .catch(e => { if (alive) setError(e?.response?.data?.message || 'Could not load your receipts'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const loadMore = async () => {
    setMore(true);
    try {
      const r = await fetchPage(slips.length);
      setSlips(prev => [...prev, ...(r.data?.receipts || [])]);
    } catch { /* button stays, nothing lost */ }
    finally { setMore(false); }
  };

  // Each slip carries the shared header/summary so combinedHTML sees
  // the shape it expects. Memoised because building thirty HTML strings
  // on every zoom click would be visible.
  const pages = useMemo(() => slips.map(s => ({
    key: s.payment?.payment_id,
    receiptNo: s.payment?.receipt_no || '',
    date: s.payment?.pay_date || '',
    amount: Number(s.payment?.total_amount || 0),
    html: combinedHTML({
      school:  data?.school  || {},
      student: data?.student || {},
      summary: data?.summary || {},
      accounts: data?.accounts || [],
      payment: s.payment,
      lines:   s.lines,
      history: s.history,
      history_scope: s.history_scope,
    }),
  })), [slips, data]);

  // Which page is in view. Reported by the pages themselves through an
  // observer rather than measured on every scroll event — a scroll
  // handler doing getBoundingClientRect on thirty sheets is how a
  // smooth list starts stuttering.
  useEffect(() => {
    if (!pages.length || typeof IntersectionObserver === 'undefined') return undefined;
    const root = scrollRef.current || null;
    const io = new IntersectionObserver((entries) => {
      const seen = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!seen) return;
      const i = Number(seen.target.getAttribute('data-page'));
      if (!Number.isNaN(i)) setActive(i);
    }, { root, threshold: [0.25, 0.5, 0.75] });
    pageRefs.current.slice(0, pages.length).forEach(el => el && io.observe(el));
    return () => io.disconnect();
  }, [pages.length]);

  // Keep the rail's highlighted thumbnail on screen as the middle scrolls.
  useEffect(() => {
    railRefs.current[active]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [active]);

  const goTo = useCallback((i) => {
    pageRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(i);
  }, []);

  // Print every slip, not just the one on screen — the browser paginates
  // them because each sheet is a fixed A5 block with its own page break.
  const printAll = () => {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.write(
      `<html><head><title>Receipts</title><style>${RECEIPT_CSS}
       .receipt-sheet { page-break-after: always; }</style></head>
       <body>${pages.map(p => p.html).join('')}</body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={44} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 2 }} />
      </Stack>
    );
  }
  if (error) return <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>;
  if (!pages.length) {
    return (
      <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', boxShadow: 'none',
        border: '1px dashed', borderColor: P.line }}>
        <ReceiptIcon sx={{ fontSize: 40, color: P.line }} />
        <Typography sx={{ fontWeight: 800, color: P.ink, mt: 1 }}>No receipt slips yet</Typography>
        <Typography sx={{ fontSize: 13, color: P.inkSoft, mt: .5 }}>
          A slip is produced each time the school records a payment.
        </Typography>
      </Card>
    );
  }

  // Fit the sheet to whatever width is available, then apply the user's
  // zoom on top. transform-origin top-left plus a matching height on the
  // wrapper keeps the layout honest — a scaled child that still reserves
  // its unscaled height leaves a large gap under every page.
  const railW = isDesktop ? 132 : 0;
  const avail = isDesktop ? 640 : 320;
  const fit   = Math.min(1, avail / SHEET_PX);
  const scale = fit * zoom;

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ flex: 1, fontSize: 13, color: P.inkSoft }}>
          Page <b style={{ color: P.ink }}>{active + 1}</b> of {pages.length}
          {data?.total > pages.length && ` · ${data.total} in total`}
        </Typography>
        <Tooltip title="Zoom out">
          <span>
            <IconButton size="small" disabled={zoom <= 0.6}
              onClick={() => setZoom(z => Math.max(0.6, +(z - 0.2).toFixed(2)))}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: P.inkSoft, width: 42, textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton size="small" disabled={zoom >= 2}
              onClick={() => setZoom(z => Math.min(2, +(z + 0.2).toFixed(2)))}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button size="small" startIcon={<PrintIcon />} onClick={printAll}
          sx={{ fontWeight: 800, textTransform: 'none', color: P.brandDeep }}>
          Print all
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="flex-start">
        {/* ── Page rail ── */}
        {isDesktop && (
          <Box sx={{
            width: railW, flexShrink: 0,
            maxHeight: 'calc(100vh - 250px)', overflowY: 'auto',
            pr: .5, '&::-webkit-scrollbar': { width: 6 },
          }}>
            <Stack spacing={1}>
              {pages.map((p, i) => (
                <Box key={p.key || i}
                  ref={el => { railRefs.current[i] = el; }}
                  onClick={() => goTo(i)}
                  sx={{
                    cursor: 'pointer', borderRadius: 1.5, overflow: 'hidden',
                    border: '2px solid',
                    borderColor: active === i ? P.brand : P.line,
                    bgcolor: '#fff',
                    transition: 'border-color .15s',
                    '&:hover': { borderColor: P.brand },
                  }}>
                  {/* A real miniature of the sheet, not a placeholder —
                      it is the same HTML at 15%, so the parent can tell
                      pages apart by shape. */}
                  <Box sx={{ height: 84, overflow: 'hidden', position: 'relative', bgcolor: '#fff' }}>
                    <Box
                      sx={{
                        transform: 'scale(0.152)', transformOrigin: 'top left',
                        width: SHEET_PX, pointerEvents: 'none',
                      }}
                      dangerouslySetInnerHTML={{ __html: p.html }}
                    />
                  </Box>
                  <Box sx={{
                    px: .8, py: .6,
                    bgcolor: active === i ? P.brandSoft : '#FAFBFC',
                    borderTop: '1px solid', borderColor: P.line,
                  }}>
                    <Typography noWrap sx={{
                      fontSize: 10, fontWeight: 800,
                      color: active === i ? P.brandDeep : P.ink,
                    }}>
                      {i + 1}. {p.receiptNo || '—'}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: 9.5, color: P.inkSoft }}>
                      {fmtDate(p.date)} · {money(p.amount)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* ── The pages ── */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1, minWidth: 0,
            maxHeight: { xs: 'none', md: 'calc(100vh - 250px)' },
            overflowY: { xs: 'visible', md: 'auto' },
            overflowX: 'auto',
            bgcolor: '#5A5A5F', borderRadius: 2, p: { xs: 1.2, md: 2 },
            '&::-webkit-scrollbar': { width: 8, height: 8 },
          }}>
          <Stack spacing={2} alignItems="center">
            {pages.map((p, i) => (
              <Box key={p.key || i}
                data-page={i}
                ref={el => { pageRefs.current[i] = el; }}
                sx={{ width: SHEET_PX * scale, flexShrink: 0 }}>
                <Box sx={{
                  width: SHEET_PX * scale,
                  // 148mm at the same ratio the width was scaled by.
                  height: SHEET_PX * (148 / 210) * scale,
                  overflow: 'hidden',
                  boxShadow: '0 4px 18px rgba(0,0,0,.35)',
                  bgcolor: '#fff',
                }}>
                  <Box
                    sx={{
                      transform: `scale(${scale})`, transformOrigin: 'top left',
                      width: SHEET_PX,
                    }}
                    dangerouslySetInnerHTML={{ __html: p.html }}
                  />
                </Box>
                <Typography sx={{
                  textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,.75)',
                  mt: .6, fontWeight: 600,
                }}>
                  {i + 1} / {pages.length}
                </Typography>
              </Box>
            ))}

            {data?.total > pages.length && (
              <Button onClick={loadMore} disabled={more} variant="contained"
                sx={{
                  fontWeight: 800, textTransform: 'none', borderRadius: 3, px: 3, my: 1,
                  bgcolor: P.brand, color: P.ink, '&:hover': { bgcolor: P.brandDeep },
                }}>
                {more ? 'Loading…' : `Load more (${data.total - pages.length} left)`}
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>

      {/* The one style block the sheets need. Scoped by the .receipt-*
          class names the markup already uses. */}
      <style>{RECEIPT_CSS}</style>
    </Box>
  );
}
